import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PawPrint, Trash2, Search, Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_TONE = {
  pending: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  approved: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  denied: "border-rose-500/40 text-rose-300 bg-rose-500/10",
};

export default function PetRegistryAdminPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [residents, setResidents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    resident_id: "", pet_name: "", species: "dog", breed: "", weight_lbs: "", approve_immediately: true,
  });

  const load = useCallback(async () => {
    if (!propertyId) return;
    const { data } = await api.get(`/pets?property_id=${propertyId}`);
    setItems(data.items || []);
  }, [propertyId]);
  const loadResidents = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/admin/residents?property_id=${propertyId}`);
      setResidents(data.items || []);
    } catch { setResidents([]); }
  }, [propertyId]);
  useEffect(() => { load(); loadResidents(); }, [load, loadResidents]);

  const openNew = () => {
    setForm({ resident_id: "", pet_name: "", species: "dog", breed: "", weight_lbs: "", approve_immediately: true });
    setDialogOpen(true);
  };
  const savePet = async () => {
    if (!form.resident_id || !form.pet_name.trim() || !form.species.trim()) {
      return toast.error("Resident, pet name, and species are required");
    }
    const resident = residents.find((r) => r.id === form.resident_id);
    try {
      // Create the pet as the resident (admin registering on their behalf)
      const { data: created } = await api.post("/admin/pets", {
        property_id: propertyId,
        resident_id: form.resident_id,
        resident_name: resident?.name,
        unit: resident?.unit || null,
        pet_name: form.pet_name.trim(),
        species: form.species.trim(),
        breed: form.breed.trim() || null,
        weight_lbs: form.weight_lbs ? Number(form.weight_lbs) : null,
        status: form.approve_immediately ? "approved" : "pending",
      });
      toast.success(form.approve_immediately ? "Pet registered and approved" : "Pet registered — pending review");
      setDialogOpen(false);
      load();
      return created;
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const decide = async (p, decision) => {
    try {
      await api.patch(`/pets/${p.id}/decide`, { decision });
      toast.success(`Marked ${decision}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const setStatusDirect = async (p, newStatus) => {
    try {
      await api.patch(`/admin/pets/${p.id}`, { status: newStatus });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.pet_name} from the registry?`)) return;
    try {
      await api.delete(`/admin/pets/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const filtered = useMemo(() => items.filter((p) => {
    if (status !== "all" && p.status !== status) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return [p.pet_name, p.species, p.breed, p.resident_name, p.unit]
      .some((v) => (v || "").toLowerCase().includes(s));
  }), [items, q, status]);

  const kpis = useMemo(() => ({
    total: items.length,
    approved: items.filter((p) => p.status === "approved").length,
    pending: items.filter((p) => p.status === "pending").length,
    denied: items.filter((p) => p.status === "denied").length,
  }), [items]);

  return (
    <div>
      <PageHeader
        eyebrow="Community"
        title="Pet Registry"
        subtitle="Every pet on this property — search, approve, edit, remove."
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-white/10">{kpis.total} total</Badge>
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10">{kpis.approved} approved</Badge>
            {kpis.pending > 0 && <Badge variant="outline" className="border-amber-500/40 text-amber-300 bg-amber-500/10">{kpis.pending} pending</Badge>}
            <Button size="sm" onClick={openNew} className="bg-orange-500 hover:bg-orange-600 gap-2" data-testid="pet-new-btn">
              <Plus className="h-4 w-4" /> Register pet
            </Button>
          </div>
        }
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-white/10" data-testid="pet-dialog">
          <DialogHeader><DialogTitle>Register a pet on behalf of a resident</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Resident</Label>
              <Select value={form.resident_id} onValueChange={(v) => setForm({ ...form, resident_id: v })}>
                <SelectTrigger data-testid="pet-form-resident"><SelectValue placeholder="Pick a resident" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {residents.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} — Unit {r.unit || "—"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Pet name</Label><Input data-testid="pet-form-name" value={form.pet_name} onChange={(e) => setForm({ ...form, pet_name: e.target.value })} placeholder="Bella" /></div>
              <div>
                <Label>Species</Label>
                <Select value={form.species} onValueChange={(v) => setForm({ ...form, species: v })}>
                  <SelectTrigger data-testid="pet-form-species"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dog">Dog</SelectItem>
                    <SelectItem value="cat">Cat</SelectItem>
                    <SelectItem value="bird">Bird</SelectItem>
                    <SelectItem value="fish">Fish</SelectItem>
                    <SelectItem value="reptile">Reptile</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Breed</Label><Input data-testid="pet-form-breed" value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} placeholder="Golden Retriever" /></div>
              <div><Label>Weight (lbs)</Label><Input data-testid="pet-form-weight" type="number" value={form.weight_lbs} onChange={(e) => setForm({ ...form, weight_lbs: e.target.value })} placeholder="45" /></div>
            </div>
            <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={form.approve_immediately} onChange={(e) => setForm({ ...form, approve_immediately: e.target.checked })} data-testid="pet-form-preapprove" />
              Approve immediately (skip pending review)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={savePet} data-testid="pet-form-save">Register</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border-white/10 bg-card p-4 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input data-testid="pet-search" className="pl-9 bg-white/[0.02] border-white/10" placeholder="Search by pet, resident, unit, breed" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="pet-status" className="w-44 bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="denied">Denied</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((p) => (
          <Card key={p.id} className="border-white/10 bg-card p-5" data-testid={`petreg-${p.id}`}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md border border-white/10 bg-white/[0.03] flex items-center justify-center">
                <PawPrint className="h-4 w-4 text-zinc-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{p.pet_name}</div>
                <div className="text-[11px] text-zinc-500 truncate">{p.species}{p.breed ? ` · ${p.breed}` : ""}{p.weight_lbs ? ` · ${p.weight_lbs} lbs` : ""}</div>
              </div>
              <Badge variant="outline" className={STATUS_TONE[p.status] || "border-white/10"}>{p.status}</Badge>
            </div>
            <div className="text-xs text-zinc-500 mt-3">{p.resident_name} · Unit {p.unit || "—"}</div>
            <div className="flex gap-2 mt-4 flex-wrap">
              {p.status === "pending" && (
                <>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(p, "approved")} data-testid={`pet-approve-${p.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" className="border-rose-500/30 text-rose-300" onClick={() => decide(p, "denied")} data-testid={`pet-deny-${p.id}`}>Deny</Button>
                </>
              )}
              {p.status === "approved" && (
                <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => setStatusDirect(p, "denied")} data-testid={`pet-revoke-${p.id}`}>Revoke</Button>
              )}
              {p.status === "denied" && (
                <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => setStatusDirect(p, "approved")} data-testid={`pet-reapprove-${p.id}`}>Re-approve</Button>
              )}
              <Button size="sm" variant="outline" className="border-rose-500/30 text-rose-300 ml-auto" onClick={() => remove(p)} data-testid={`pet-delete-${p.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && (
          <Card className="col-span-full border-dashed border-white/10 p-10 text-center text-muted-foreground">No pets match.</Card>
        )}
      </div>
    </div>
  );
}
